import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

// API Base URL - Configure this for production
// For local: http://localhost:8787
// For production: Your Cloudflare Worker URL
const API_URL = process.env.REACT_APP_API_URL || 'https://lapero-marrokii-api.redaezzin9.workers.dev/api';

// Auth Context
const AuthContext = React.createContext(null);

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast ${type}`}>
      <i className={`toast-icon fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
      <span className="toast-message">{message}</span>
    </div>
  );
};

// Login Page
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/auth/login`, { username, password });
      if (res.data.success) {
        login(res.data.token, res.data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box fade-in">
        <div className="login-logo">
          <div className="logo-icon">M</div>
          <h1>L'APÉRO MARROKII</h1>
          <p>Admin Control Panel</p>
        </div>

        <div className={`login-error ${error ? 'show' : ''}`}>{error}</div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Access Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activePage, setActivePage, onLogout }) => {
  const menuItems = [
    { id: 'dashboard', icon: 'fa-th-large', label: 'Dashboard' },
    { id: 'reservations', icon: 'fa-calendar-alt', label: 'Reservations', badge: true },
    { id: 'messages', icon: 'fa-envelope', label: 'Messages', badge: true },
    { id: 'menu', icon: 'fa-utensils', label: 'Menu Management' },
    { id: 'content', icon: 'fa-cogs', label: 'Website Content' },
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <div className="logo-icon">M</div>
          <div className="logo-text">
            <h2>L'APÉRO</h2>
            <span>Marrokii Admin</span>
          </div>
        </div>
      </div>

      <nav className="nav-menu">
        <div className="nav-section">
          <div className="nav-section-title">Main</div>
          {menuItems.slice(0, 1).map(item => (
            <div
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              <i className={`fas ${item.icon}`}></i>
              {item.label}
            </div>
          ))}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Management</div>
          {menuItems.slice(1, 3).map(item => (
            <div
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              <i className={`fas ${item.icon}`}></i>
              {item.label}
              {item.badge && <span className="badge">3</span>}
            </div>
          ))}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Control</div>
          {menuItems.slice(3).map(item => (
            <div
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              <i className={`fas ${item.icon}`}></i>
              {item.label}
            </div>
          ))}
        </div>
      </nav>

      <div style={{ padding: '16px 12px' }}>
        <div className="nav-item" onClick={onLogout}>
          <i className="fas fa-sign-out-alt"></i>
          Logout
        </div>
      </div>
    </aside>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: { Authorization: token }
      });
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  return (
    <div className="content fade-in">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon pink">
            <i className="fas fa-calendar-check"></i>
          </div>
          <h3>{stats?.totalReservations || 0}</h3>
          <p>Total Reservations</p>
          <span className="trend up">+12%</span>
        </div>

        <div className="stat-card">
          <div className="icon purple">
            <i className="fas fa-envelope-open-text"></i>
          </div>
          <h3>{stats?.totalMessages || 0}</h3>
          <p>Total Messages</p>
        </div>

        <div className="stat-card">
          <div className="icon blue">
            <i className="fas fa-calendar-day"></i>
          </div>
          <h3>{stats?.todayReservations || 0}</h3>
          <p>Today's Reservations</p>
        </div>

        <div className="stat-card">
          <div className="icon green">
            <i className="fas fa-check-circle"></i>
          </div>
          <h3>{stats?.confirmedReservations || 0}</h3>
          <p>Confirmed</p>
        </div>
      </div>
    </div>
  );
};

// Reservations Page
const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    fetchReservations();
  }, [token]);

  const fetchReservations = async () => {
    try {
      const res = await axios.get(`${API_URL}/reservations`, {
        headers: { Authorization: token }
      });
      setReservations(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/reservations/${id}`, { status }, {
        headers: { Authorization: token }
      });
      fetchReservations();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm('Delete this reservation?')) return;
    try {
      await axios.delete(`${API_URL}/reservations/${id}`, {
        headers: { Authorization: token }
      });
      fetchReservations();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  return (
    <div className="content fade-in">
      <div className="card">
        <div className="card-header">
          <h2>All Reservations</h2>
        </div>
        <div className="card-body">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Guests</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '40px' }}>
                      No reservations yet
                    </td>
                  </tr>
                ) : (
                  reservations.map(r => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.phone}</td>
                      <td>{new Date(r.date).toLocaleDateString()}</td>
                      <td>{r.guests}</td>
                      <td>{r.experienceType || r.experience_type}</td>
                      <td>
                        <span className={`status-badge ${r.status.toLowerCase()}`}>{r.status}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {r.status === 'Pending' && (
                            <button className="btn btn-icon success" onClick={() => updateStatus(r.id, 'Confirmed')}>
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          <button className="btn btn-icon danger" onClick={() => deleteReservation(r.id)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Messages Page
const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: token }
      });
      setMessages(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/messages/${id}`, {
        headers: { Authorization: token }
      });
      fetchMessages();
    } catch (err) {
      console.error(err);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/messages/${id}/read`, {}, {
        headers: { Authorization: token }
      });
      fetchMessages();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  return (
    <div className="content fade-in">
      <div className="card">
        <div className="card-header">
          <h2>Contact Messages</h2>
        </div>
        <div className="card-body">
          {messages.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-envelope-open"></i>
              <h3>No messages yet</h3>
              <p>Messages from the contact form will appear here</p>
            </div>
          ) : (
            messages.map(m => (
              <div key={m.id} className="message-item">
                <div className="message-header">
                  <h4>{m.name}</h4>
                  <span className="date">{new Date(m.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="message-body">
                  <p>{m.message}</p>
                  <div className="message-contact">
                    <span><i className="fas fa-envelope"></i> {m.email}</span>
                    {m.phone && <span><i className="fas fa-phone"></i> {m.phone}</span>}
                  </div>
                  <div className="message-actions">
                    {!m.isRead && (
                      <button className="btn btn-outline btn-sm" onClick={() => markAsRead(m.id)}>
                        Mark as Read
                      </button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => deleteMessage(m.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Menu Management Page
const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { token } = React.useContext(AuthContext);

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'cocktails',
    image: ''
  });

  useEffect(() => {
    fetchMenu();
  }, [token]);

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API_URL}/menu`, {
        headers: { Authorization: token }
      });
      setMenuItems(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        name: item.name,
        description: item.description || '',
        price: item.price,
        category: item.category,
        image: item.image || ''
      });
    } else {
      setEditingItem(null);
      setForm({ name: '', description: '', price: '', category: 'cocktails', image: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/menu/${editingItem.id}`, form, {
          headers: { Authorization: token }
        });
      } else {
        await axios.post(`${API_URL}/menu`, form, {
          headers: { Authorization: token }
        });
      }
      setShowModal(false);
      fetchMenu();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await axios.delete(`${API_URL}/menu/${id}`, {
        headers: { Authorization: token }
      });
      fetchMenu();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  const categories = ['cocktails', 'shisha', 'bites', 'vip'];
  const categoryLabels = { cocktails: 'Soul Sessions', shisha: 'Cloud Nine', bites: 'Midnight Tastings', vip: 'The Collection' };

  return (
    <div className="content fade-in">
      <div className="card">
        <div className="card-header">
          <h2>Menu Management</h2>
          <button className="btn btn-primary btn-sm" onClick={() => openModal()}>
            <i className="fas fa-plus"></i> Add Item
          </button>
        </div>
        <div className="menu-grid">
          {menuItems.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              <i className="fas fa-utensils"></i>
              <h3>No menu items yet</h3>
              <p>Add your first menu item to get started</p>
            </div>
          ) : (
            menuItems.map(item => (
              <div key={item.id} className="menu-card">
                <div className="menu-card-image" style={{ backgroundImage: item.image ? `url(${item.image})` : 'none' }}>
                  {!item.image && <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)' }}><i className="fas fa-image fa-2x"></i></div>}
                </div>
                <div className="menu-card-content">
                  <div className="category">{categoryLabels[item.category]}</div>
                  <h4>{item.name}</h4>
                  <p>{item.description}</p>
                  <div className="price">{item.price}</div>
                </div>
                <div className="menu-card-actions">
                  <button className="btn btn-outline btn-sm" onClick={() => openModal(item)}>Edit</button>
                  <button className="btn btn-outline btn-sm" onClick={() => deleteMenuItem(item.id)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Price</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{categoryLabels[cat]}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Image URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.image}
                    onChange={e => setForm({ ...form, image: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingItem ? 'Save Changes' : 'Add Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Content Management Page
const ContentManagement = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    fetchContent();
  }, [token]);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/site-content`, {
        headers: { Authorization: token }
      });
      setContent(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/site-content`, content, {
        headers: { Authorization: token }
      });
      alert('Content saved successfully!');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  return (
    <div className="content fade-in">
      <div className="card">
        <div className="card-header">
          <h2>Website Content Control</h2>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit}>
            <i className="fas fa-save"></i> Save Changes
          </button>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', padding: '24px' }}>
            <div className="form-group">
              <label className="form-label">Hero Title</label>
              <input
                type="text"
                className="form-input"
                value={content.heroTitle || ''}
                onChange={e => setContent({ ...content, heroTitle: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Hero Subtitle</label>
              <input
                type="text"
                className="form-input"
                value={content.heroSubtitle || ''}
                onChange={e => setContent({ ...content, heroSubtitle: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Experience Title</label>
              <input
                type="text"
                className="form-input"
                value={content.experienceTitle || ''}
                onChange={e => setContent({ ...content, experienceTitle: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Experience Description</label>
              <input
                type="text"
                className="form-input"
                value={content.experienceDesc || ''}
                onChange={e => setContent({ ...content, experienceDesc: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Address</label>
              <input
                type="text"
                className="form-input"
                value={content.contactAddress || ''}
                onChange={e => setContent({ ...content, contactAddress: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Hours</label>
              <input
                type="text"
                className="form-input"
                value={content.contactHours || ''}
                onChange={e => setContent({ ...content, contactHours: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Layout
const Layout = () => {
  const [activePage, setActivePage] = useState('dashboard');
  const { logout, user } = React.useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const pageTitles = {
    dashboard: 'Dashboard',
    reservations: 'Reservations',
    messages: 'Messages',
    menu: 'Menu Management',
    content: 'Website Content'
  };

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <Dashboard />;
      case 'reservations': return <Reservations />;
      case 'messages': return <Messages />;
      case 'menu': return <MenuManagement />;
      case 'content': return <ContentManagement />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="app-layout">
      <Sidebar 
        activePage={activePage} 
        setActivePage={setActivePage} 
        onLogout={logout} 
      />
      
      <main className="main-content">
        <nav className="topbar">
          <div className="topbar-left">
            <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">{pageTitles[activePage]}</h1>
          </div>
          <div className="topbar-right">
            <div className="admin-profile">
              <div className="admin-avatar">{user?.username?.charAt(0).toUpperCase()}</div>
              <div className="admin-info">
                <span className="name">{user?.username}</span>
                <span className="role">Administrator</span>
              </div>
            </div>
          </div>
        </nav>

        {renderPage()}
      </main>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token } = React.useContext(AuthContext);
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App Component
const App = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [user, setUser] = useState(null);

  const login = (newToken, newUser) => {
    localStorage.setItem('adminToken', newToken);
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      <Router>
        <Routes>
          <Route path="/login" element={token ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
